import React, { useState, useEffect, useContext } from "react";
import {
  Container,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Typography,
  IconButton,
  CircularProgress,
  withStyles,
  Card,
  ListItem,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { Edit as EditIcon } from "@material-ui/icons";
import { useParams } from "react-router-dom";
import { Instance } from "../../../Config";
import OwnerHeader from "../Components/OwnerHeader";
import GlobalContext from "../../../Context/globalContext";
import { SHOW_SNACKBAR } from "../../../Context/globalActions.types";
import { Skeleton } from "@material-ui/lab";

const useStyles = makeStyles((theme) => ({
  paper: {
    width: "100%",
    marginBottom: theme.spacing(2),
  },
  tableContainer: {
    maxHeight: 600,
  },
}));

const StyledTableCell = withStyles((theme) => ({
  head: {
    backgroundColor: "#3f51b5",
    color: theme.palette.common.white,
    fontWeight: 600,
  },
}))(TableCell);

const StyledTableRow = withStyles((theme) => ({
  root: {
    "&:nth-of-type(even)": {
      backgroundColor: theme.palette.action.hover,
    },
    "&:hover": {
      backgroundColor: "#F3F3F3",
      transform: "scale(1.0019)",
    },
  },
}))(TableRow);

const CategoryTranslations = () => {
  const classes = useStyles();
  const { mobileNo } = useParams();
  const { dispatch } = useContext(GlobalContext);
  const [languages, setLanguages] = useState([]);
  const [selectedLang, setSelectedLang] = useState("");
  const [categories, setCategories] = useState([]);
  const [editingCategory, setEditingCategory] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [openSuccessDialog, setOpenSuccessDialog] = useState(false);
  const [openErrorDialog, setOpenErrorDialog] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [dataLoading, setDataLoading] = useState(false);

  // Form for translation
  const [translationForm, setTranslationForm] = useState({
    dept_name: "",
    ITEMDESC: "",
  });

  useEffect(() => {
    fetchLanguages();
  }, []);

  useEffect(() => {
    if (selectedLang) {
      fetchCategories();
    }
  }, [selectedLang]);

  const fetchLanguages = async () => {
    setDataLoading(true);
    try {
      const response = await Instance.get(`/languages?mobileNo=${mobileNo}`);
      const langs = Array.isArray(response.data)
        ? response.data
        : response.data?.result || response.data?.languages || [];
      setLanguages(langs);
      const nonEnglish = langs.find((l) => l.lang_code !== "en");
      if (nonEnglish) {
        setSelectedLang(nonEnglish.lang_code);
      }
    } catch (error) {
      console.error("Error fetching languages:", error);
      dispatch({
        type: SHOW_SNACKBAR,
        payload: {
          message: "Error fetching languages",
          type: "error",
        },
      });
    } finally {
      setDataLoading(false);
    }
  };

  const fetchCategories = async () => {
    setDataLoading(true);
    try {
      const response = await Instance.get(`/dept?mobileNo=${mobileNo}`);
      const depts = response.data.result || [];

      // Fetch translations for all categories
      if (selectedLang) {
        const translationsPromises = depts.map((dept) =>
          fetchCategoryTranslation(dept.DEPT_CD)
        );
        const translationsResults = await Promise.all(translationsPromises);

        const deptsWithTranslations = depts.map((dept, index) => ({
          ...dept,
          translation: translationsResults[index],
        }));
        setCategories(deptsWithTranslations);
      } else {
        setCategories(depts);
      }
    } catch (error) {
      console.error("Error fetching categories:", error);
      dispatch({
        type: SHOW_SNACKBAR,
        payload: {
          message: "Error fetching categories",
          type: "error",
        },
      });
    } finally {
      setDataLoading(false);
    }
  };

  const fetchCategoryTranslation = async (deptCd) => {
    if (!selectedLang) return null;
    try {
      const response = await Instance.get(
        `/translations/category/${deptCd}?mobileNo=${mobileNo}&lang_code=${selectedLang}`
      );
      return response.data.find((t) => t.lang_code === selectedLang) || null;
    } catch (error) {
      return null;
    }
  };

  const handleEditCategory = async (category) => {
    setEditingCategory(category);
    const translation = category.translation;
    setTranslationForm({
      dept_name: translation?.dept_name || "",
      ITEMDESC: translation?.ITEMDESC || "",
    });
    setOpenDialog(true);
  };

  const handleSaveTranslation = async () => {
    setLoading(true);
    try {
      await Instance.post(`/translations/category`, {
        DEPT_CD: editingCategory.DEPT_CD,
        lang_code: selectedLang,
        ...translationForm,
      });
      setOpenDialog(false);
      setOpenSuccessDialog(true);
      fetchCategories();
    } catch (error) {
      console.error("Error saving translation:", error);
      setErrorMessage(
        error.response?.data?.message || "Error saving translation"
      );
      setOpenErrorDialog(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <OwnerHeader>
        <div className="md:flex items-center justify-between px-3 md:px-4 py-3">
          <div>
            <div className="text-indigo-500 font-semibold text-sm md:text-base">
              Translation Management
            </div>
            <div className="text-xl md:text-2xl font-extrabold leading-7 sm:leading-10 truncate mt-1">
              Category Translations
            </div>
          </div>
        </div>

        <div className="py-1 min-h-screen bg-[#F1F5F9] px-2">
          <Card>
            <ListItem className="pb-0">
              <Typography
                variant="h6"
                className="text-indigo-500 uppercase font-bold tracking-wider text-xs"
              >
                Filters
              </Typography>
            </ListItem>
            <ListItem className="pt-0">
              <Typography
                variant="h6"
                className="text-gray-500 uppercase font-bold tracking-wider text-[9px]"
              >
                Select language to view and edit translations
              </Typography>
            </ListItem>
          </Card>

          <div className="mx-2 md:flex items-end gap-4 mb-4">
            <FormControl
              variant="outlined"
              className="md:w-64 w-full"
              margin="normal"
            >
              <InputLabel>Language</InputLabel>
              <Select
                value={selectedLang}
                onChange={(e) => setSelectedLang(e.target.value)}
                label="Language"
                className="bg-white"
              >
                {languages
                  .filter((l) => l.lang_code !== "en")
                  .map((lang) => (
                    <MenuItem key={lang.lang_code} value={lang.lang_code}>
                      {lang.lang_name} ({lang.native_name})
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>
            <Button
              variant="contained"
              disableElevation
              className="mb-2 bg-[#3333cc] text-white h-[56px]"
              onClick={fetchCategories}
              disabled={!selectedLang}
            >
              Apply
            </Button>
          </div>

          <React.Fragment>
            <TableContainer
              style={{
                overflowX: "clip",
              }}
              component={Paper}
            >
              <div className="md:flex justify-between w-full my-3 px-2 pt-3">
                <Typography className="md:ml-2 text-md md:text-lg mb-2 font-medium">
                  Category Translations
                </Typography>
              </div>

              <div className="max-h-screen overflow-x-hidden">
                <Table stickyHeader>
                  <TableHead>
                    <TableRow>
                      <StyledTableCell>Code</StyledTableCell>
                      <StyledTableCell>English Name</StyledTableCell>
                      <StyledTableCell>
                        {languages.find((l) => l.lang_code === selectedLang)
                          ?.lang_name || selectedLang}{" "}
                        Name
                      </StyledTableCell>
                      <StyledTableCell>English Description</StyledTableCell>
                      <StyledTableCell>
                        {languages.find((l) => l.lang_code === selectedLang)
                          ?.lang_name || selectedLang}{" "}
                        Description
                      </StyledTableCell>
                      <StyledTableCell>Actions</StyledTableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {dataLoading && categories.length === 0 ? (
                      Array.from(new Array(5)).map((_, index) => (
                        <StyledTableRow key={index}>
                          <StyledTableCell>
                            <Skeleton />
                          </StyledTableCell>
                          <StyledTableCell>
                            <Skeleton />
                          </StyledTableCell>
                          <StyledTableCell>
                            <Skeleton />
                          </StyledTableCell>
                          <StyledTableCell>
                            <Skeleton />
                          </StyledTableCell>
                          <StyledTableCell>
                            <Skeleton />
                          </StyledTableCell>
                          <StyledTableCell>
                            <Skeleton />
                          </StyledTableCell>
                        </StyledTableRow>
                      ))
                    ) : !dataLoading && categories.length === 0 ? (
                      <StyledTableRow>
                        <TableCell colSpan={6} align="center">
                          <Typography variant="body2" color="textSecondary">
                            No categories found
                          </Typography>
                        </TableCell>
                      </StyledTableRow>
                    ) : (
                      categories.map((category) => (
                        <StyledTableRow key={category.DEPT_CD}>
                          <TableCell>{category.DEPT_CD}</TableCell>
                          <TableCell>{category.dept_name}</TableCell>
                          <TableCell>
                            {category.translation?.dept_name || (
                              <em style={{ color: "#999" }}>Not translated</em>
                            )}
                          </TableCell>
                          <TableCell>{category.ITEMDESC}</TableCell>
                          <TableCell>
                            {category.translation?.ITEMDESC || (
                              <em style={{ color: "#999" }}>Not translated</em>
                            )}
                          </TableCell>
                          <TableCell>
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={() => handleEditCategory(category)}
                            >
                              <EditIcon />
                            </IconButton>
                          </TableCell>
                        </StyledTableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>
            </TableContainer>
          </React.Fragment>

          {/* Edit Translation Dialog */}
          <Dialog
            open={openDialog}
            onClose={() => setOpenDialog(false)}
            maxWidth="md"
            fullWidth
          >
            <DialogTitle>
              Edit Translation - {editingCategory?.dept_name}
            </DialogTitle>
            <DialogContent>
              <TextField
                label="Category Name"
                fullWidth
                margin="normal"
                variant="outlined"
                value={translationForm.dept_name}
                onChange={(e) =>
                  setTranslationForm({
                    ...translationForm,
                    dept_name: e.target.value,
                  })
                }
                helperText={`Original: ${editingCategory?.dept_name}`}
              />

              <TextField
                label="Description"
                fullWidth
                margin="normal"
                variant="outlined"
                multiline
                rows={4}
                value={translationForm.ITEMDESC}
                onChange={(e) =>
                  setTranslationForm({
                    ...translationForm,
                    ITEMDESC: e.target.value,
                  })
                }
                helperText={`Original: ${editingCategory?.ITEMDESC}`}
              />
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setOpenDialog(false)} color="default">
                Cancel
              </Button>
              <Button
                onClick={handleSaveTranslation}
                color="primary"
                variant="contained"
                disableElevation
                disabled={loading}
                className="bg-[#3333cc]"
              >
                {loading ? (
                  <>
                    <CircularProgress size={20} className="mr-2" /> Saving...
                  </>
                ) : (
                  "Save Translation"
                )}
              </Button>
            </DialogActions>
          </Dialog>

          {/* Success Dialog */}
          <Dialog
            open={openSuccessDialog}
            onClose={() => setOpenSuccessDialog(false)}
            maxWidth="sm"
            fullWidth
          >
            <DialogTitle>Success</DialogTitle>
            <DialogContent>
              <Typography>Translation saved successfully!</Typography>
            </DialogContent>
            <DialogActions>
              <Button
                onClick={() => setOpenSuccessDialog(false)}
                color="primary"
                variant="contained"
                disableElevation
                className="bg-[#3333cc]"
              >
                OK
              </Button>
            </DialogActions>
          </Dialog>

          {/* Error Dialog */}
          <Dialog
            open={openErrorDialog}
            onClose={() => setOpenErrorDialog(false)}
            maxWidth="sm"
            fullWidth
          >
            <DialogTitle>Error</DialogTitle>
            <DialogContent>
              <Typography>{errorMessage}</Typography>
            </DialogContent>
            <DialogActions>
              <Button
                onClick={() => setOpenErrorDialog(false)}
                color="secondary"
                variant="contained"
                disableElevation
              >
                OK
              </Button>
            </DialogActions>
          </Dialog>
        </div>
      </OwnerHeader>
    </>
  );
};

export default CategoryTranslations;
